/** HTTP-backed UGC API. Hosts provide backend auth and request dependencies. */
import {
  UgcApi,
  UgcEntry,
  UgcCreateParams,
  UgcUpdateParams,
  UgcListParams,
  UgcListResponse,
  UgcCollaboratorParams,
  UgcMembersResponse,
  UgcBrowseParams,
  UgcBrowseResponse,
  UgcGetManyParams,
  UgcGetManyResponse,
  UgcReportParams,
  UgcCheckTextOptions,
  UgcCheckTextResult,
  UgcCountParams,
  UgcFollowCounts,
  UgcCrossAppBrowseParams,
  UgcCrossAppCreateParams,
  UgcCrossAppUpdateParams,
  UgcVotingApi,
  UgcVoteParams,
  UgcUnvoteParams,
  UgcVoteMutationResult,
  UgcMyVotesResponse,
  UgcVoteListParams,
  UgcVoteWinnersListParams,
  UgcVoteLeaderboardResponse,
  UgcVoteWinnersResponse,
  UgcRecommendationResponse,
  UgcCompositeRecommendationResponse,
} from './UgcApi'
import { checkText } from './checkText'
import { authedBackendFetch } from '../http/backendHttpDeps'
import type { BackendHttpDeps, BackendHttpMethod } from '../http/backendHttpDeps'

/**
 * Append a contentType filter to a URLSearchParams. Arrays repeat the key
 * (`?contentType=a&contentType=b`) — Express/Cloud Run parses repeats into
 * `string[]` via the `extended: true` body parser. An empty array adds
 * nothing (matches the server's "empty == no filter" semantics).
 */
function appendContentTypeParam(
  qs: URLSearchParams,
  value: string | string[] | undefined,
): void {
  if (value === undefined) return
  if (Array.isArray(value)) {
    for (const v of value) qs.append('contentType', v)
    return
  }
  qs.set('contentType', value)
}

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined).
 */
function omitUndefined<T extends Record<string, unknown>>(obj: T): Partial<T> {
  const result: Partial<T> = {}
  for (const key of Object.keys(obj) as Array<keyof T>) {
    if (obj[key] !== undefined) {
      result[key] = obj[key]
    }
  }
  return result
}

export class HttpUgcApi implements UgcApi {
  private readonly basePath: string
  private readonly deps: BackendHttpDeps

  constructor(appId: string, deps: BackendHttpDeps) {
    this.basePath = `/v1/ugc/${encodeURIComponent(appId)}`
    this.deps = deps
  }

  private async request<TResponse>(
    method: BackendHttpMethod,
    path: string,
    body?: Record<string, unknown>,
  ): Promise<TResponse> {
    const response = await authedBackendFetch(this.deps, method, path, body)
    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = `Cloud Run request failed (${response.status})`
      try {
        const errorJson: unknown = JSON.parse(errorText)
        if (errorJson === null) {
          if (errorText) errorMessage = errorText
        } else if (typeof errorJson === 'object') {
          const error = 'error' in errorJson ? errorJson.error : undefined
          const message = 'message' in errorJson ? errorJson.message : undefined
          errorMessage = String(error || message || errorMessage)
        }
      } catch {
        if (errorText) errorMessage = errorText
      }
      throw new Error(errorMessage)
    }

    const text = await response.text()
    if (!text) return {} as TResponse
    return JSON.parse(text) as TResponse
  }

  async create(params: UgcCreateParams): Promise<UgcEntry> {
    return this.request<UgcEntry>('POST', `${this.basePath}/entries`, omitUndefined({
      contentType: params.contentType,
      data: params.data,
      isPublic: params.isPublic,
      title: params.title,
      tags: params.tags,
      isPlatformVisible: params.isPlatformVisible,
      thumbnailUrl: params.thumbnailUrl,
    }))
  }

  async update(params: UgcUpdateParams): Promise<UgcEntry> {
    return this.request<UgcEntry>('PUT', `${this.basePath}/entries/${params.id}`, omitUndefined({
      data: params.data,
      isPublic: params.isPublic,
      title: params.title,
      tags: params.tags,
      isPlatformVisible: params.isPlatformVisible,
      thumbnailUrl: params.thumbnailUrl,
    }))
  }

  async delete(id: string): Promise<void> {
    await this.request<void>('DELETE', `${this.basePath}/entries/${id}`)
  }

  async get(id: string): Promise<UgcEntry | null> {
    try {
      return await this.request<UgcEntry>('GET', `${this.basePath}/entries/${id}`)
    } catch (error: unknown) {
      // Return null for not found errors (case-insensitive, also handles 404 status)
      if (error instanceof Error) {
        const msg = error.message.toLowerCase()
        if (msg.includes('not found') || msg.includes('(404)')) {
          return null
        }
      }
      throw error
    }
  }

  async listMine(params?: UgcListParams): Promise<UgcListResponse> {
    const queryParams = new URLSearchParams()
    appendContentTypeParam(queryParams, params?.contentType)
    if (params?.cursor) queryParams.set('cursor', params.cursor)
    if (params?.limit) queryParams.set('limit', String(params.limit))

    const query = queryParams.toString()
    const endpoint = `${this.basePath}/mine${query ? `?${query}` : ''}`

    return this.request<UgcListResponse>('GET', endpoint)
  }

  // Collaborators (multi-editor)

  async listShared(params?: UgcListParams): Promise<UgcListResponse> {
    return this.request<UgcListResponse>(
      'POST',
      `${this.basePath}/shared`,
      omitUndefined({
        contentType: params?.contentType,
        cursor: params?.cursor,
        limit: params?.limit,
      }),
    )
  }

  async addCollaborator(params: UgcCollaboratorParams): Promise<void> {
    await this.request<void>(
      'POST',
      `${this.basePath}/entries/${params.entryId}/members`,
      { editorId: params.profileId },
    )
  }

  async removeCollaborator(params: UgcCollaboratorParams): Promise<void> {
    await this.request<void>(
      'DELETE',
      `${this.basePath}/entries/${params.entryId}/members/${params.profileId}`,
    )
  }

  async listCollaborators(entryId: string): Promise<UgcMembersResponse> {
    return this.request<UgcMembersResponse>(
      'GET',
      `${this.basePath}/entries/${entryId}/members`,
    )
  }

  async browse(params?: UgcBrowseParams): Promise<UgcBrowseResponse> {
    // Browse uses POST to pass body params (matching the route definition)
    return this.request<UgcBrowseResponse>('POST', `${this.basePath}/browse`, omitUndefined({
      contentType: params?.contentType,
      authorId: params?.authorId,
      cursor: params?.cursor,
      limit: params?.limit,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      filters: params?.filters,
    }))
  }

  async getMany(params: UgcGetManyParams): Promise<UgcGetManyResponse> {
    return this.request<UgcGetManyResponse>('POST', `${this.basePath}/get-many`, {
      entryIds: params.entryIds,
    })
  }

  // Phase 2: Engagement methods

  async like(id: string): Promise<{ likeCount: number }> {
    return this.request<{ likeCount: number }>('POST', `${this.basePath}/entries/${id}/like`)
  }

  async unlike(id: string): Promise<{ likeCount: number }> {
    return this.request<{ likeCount: number }>('DELETE', `${this.basePath}/entries/${id}/like`)
  }

  async recordUse(id: string): Promise<{ useCount: number }> {
    return this.request<{ useCount: number }>('POST', `${this.basePath}/entries/${id}/use`)
  }

  async report(params: UgcReportParams): Promise<void> {
    await this.request<void>('POST', `${this.basePath}/entries/${params.id}/report`, omitUndefined({
      reason: params.reason,
      details: params.details,
      supportEmail: params.supportEmail,
      metadata: params.metadata,
    }))
  }

  // Runs locally — no Cloud Run call.
  async checkTextAsync(text: string, options?: UgcCheckTextOptions): Promise<UgcCheckTextResult> {
    return checkText(text, options)
  }

  async count(params?: UgcCountParams): Promise<{ count: number }> {
    const queryParams = new URLSearchParams()
    // contentType can come from params.contentType (string | string[]) or be
    // mistakenly placed under params.filters.contentType. Honour both, but
    // route through appendContentTypeParam so arrays repeat the key rather
    // than collapsing via String() into a comma-joined garbage value.
    const contentType = params?.contentType ?? (params?.filters?.contentType as string | string[] | undefined)
    appendContentTypeParam(queryParams, contentType)
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (key === 'contentType') continue
        if (value !== undefined) {
          queryParams.set(key, String(value))
        }
      }
    }
    const query = queryParams.toString()
    const endpoint = `${this.basePath}/count${query ? `?${query}` : ''}`
    return this.request<{ count: number }>('GET', endpoint)
  }

  // Creator following

  async follow(targetProfileId: string): Promise<void> {
    await this.request<void>('POST', `${this.basePath}/creators/${targetProfileId}/follow`)
  }

  async unfollow(targetProfileId: string): Promise<void> {
    await this.request<void>('DELETE', `${this.basePath}/creators/${targetProfileId}/follow`)
  }

  async isFollowing(targetProfileId: string): Promise<{ isFollowing: boolean }> {
    return this.request<{ isFollowing: boolean }>('GET', `${this.basePath}/creators/${targetProfileId}/follow`)
  }

  async getFollowCounts(targetProfileId: string): Promise<UgcFollowCounts> {
    return this.request<UgcFollowCounts>('GET', `${this.basePath}/creators/${targetProfileId}/follow-counts`)
  }

  // Cross-app UGC

  async crossAppBrowse(params: UgcCrossAppBrowseParams): Promise<UgcBrowseResponse> {
    return this.request<UgcBrowseResponse>('POST', `${this.basePath}/browse`, omitUndefined({
      targetAppId: params.targetAppId,
      contentType: params.contentType,
      authorId: params.authorId,
      cursor: params.cursor,
      limit: params.limit,
      sortBy: params.sortBy,
      sortOrder: params.sortOrder,
      filters: params.filters,
    }))
  }

  async crossAppGet(targetAppId: string, id: string): Promise<UgcEntry | null> {
    try {
      const queryParams = new URLSearchParams()
      queryParams.set('targetAppId', targetAppId)
      return await this.request<UgcEntry>('GET', `${this.basePath}/entries/${id}?${queryParams.toString()}`)
    } catch (error: unknown) {
      if (error instanceof Error) {
        const msg = error.message.toLowerCase()
        if (msg.includes('not found') || msg.includes('(404)')) {
          return null
        }
      }
      throw error
    }
  }

  async crossAppGetMany(targetAppId: string, params: UgcGetManyParams): Promise<UgcGetManyResponse> {
    return this.request<UgcGetManyResponse>('POST', `${this.basePath}/get-many`, {
      targetAppId,
      entryIds: params.entryIds,
    })
  }

  async crossAppCount(targetAppId: string, params?: UgcCountParams): Promise<{ count: number }> {
    const queryParams = new URLSearchParams()
    queryParams.set('targetAppId', targetAppId)
    appendContentTypeParam(queryParams, params?.contentType)
    if (params?.filters) {
      for (const [key, value] of Object.entries(params.filters)) {
        if (key === 'contentType') continue
        if (value !== undefined) {
          queryParams.set(key, String(value))
        }
      }
    }
    const query = queryParams.toString()
    const endpoint = `${this.basePath}/count${query ? `?${query}` : ''}`
    return this.request<{ count: number }>('GET', endpoint)
  }

  async crossAppCreate(params: UgcCrossAppCreateParams): Promise<UgcEntry> {
    return this.request<UgcEntry>('POST', `${this.basePath}/entries/cross-app-create`, omitUndefined({
      targetAppId: params.targetAppId,
      contentType: params.contentType,
      data: params.data,
      isPublic: params.isPublic,
      title: params.title,
      tags: params.tags,
      isPlatformVisible: params.isPlatformVisible,
      thumbnailUrl: params.thumbnailUrl,
      uniqueKey: params.uniqueKey,
      audience: params.audience,
    }))
  }

  async crossAppUpdate(params: UgcCrossAppUpdateParams): Promise<UgcEntry> {
    return this.request<UgcEntry>('PUT', `${this.basePath}/entries/${params.id}/cross-app-update`, omitUndefined({
      targetAppId: params.targetAppId,
      data: params.data,
      isPublic: params.isPublic,
      title: params.title,
      tags: params.tags,
      isPlatformVisible: params.isPlatformVisible,
      thumbnailUrl: params.thumbnailUrl,
      uniqueKey: params.uniqueKey,
      audience: params.audience,
    }))
  }

  async crossAppDelete(targetAppId: string, id: string): Promise<void> {
    const queryParams = new URLSearchParams()
    queryParams.set('targetAppId', targetAppId)
    await this.request<void>('DELETE', `${this.basePath}/entries/${id}/cross-app-delete?${queryParams.toString()}`)
  }

  // Recommendations

  async getUserRecommendation(count: number, filter?: string): Promise<UgcRecommendationResponse> {
    return this.request<UgcRecommendationResponse>(
      'POST',
      `${this.basePath}/recommendations/user`,
      omitUndefined({ count, filter }),
    )
  }

  async getItemRecommendation(ugcId: string, count: number, filter?: string): Promise<UgcRecommendationResponse> {
    return this.request<UgcRecommendationResponse>(
      'POST',
      `${this.basePath}/recommendations/item`,
      omitUndefined({ ugcId, count, filter }),
    )
  }

  async getCompositeRecommendation(count: number, sourceFilter?: string, resultFilter?: string): Promise<UgcCompositeRecommendationResponse> {
    return this.request<UgcCompositeRecommendationResponse>(
      'POST',
      `${this.basePath}/recommendations/composite`,
      omitUndefined({ count, sourceFilter, resultFilter }),
    )
  }

  async getMoreRecommendations(origRecommId: string, count: number): Promise<UgcRecommendationResponse> {
    return this.request<UgcRecommendationResponse>(
      'POST',
      `${this.basePath}/recommendations/next`,
      { origRecommId, count },
    )
  }

  async addDetailView(ugcId: string, recommId?: string): Promise<void> {
    await this.request<void>(
      'POST',
      `${this.basePath}/recommendations/detail-view`,
      omitUndefined({ ugcId, recommId }),
    )
  }

  async addRating(ugcId: string, rating: number, recommId?: string): Promise<void> {
    await this.request<void>(
      'POST',
      `${this.basePath}/recommendations/rating`,
      omitUndefined({ ugcId, rating, recommId }),
    )
  }

  async addCartAddition(ugcId: string, recommId?: string): Promise<void> {
    await this.request<void>(
      'POST',
      `${this.basePath}/recommendations/cart-addition`,
      omitUndefined({ ugcId, recommId }),
    )
  }

  async setViewPortion(ugcId: string, portion: number, recommId?: string): Promise<void> {
    await this.request<void>(
      'POST',
      `${this.basePath}/recommendations/view-portion`,
      omitUndefined({ ugcId, portion, recommId }),
    )
  }

  // Weekly voting

  readonly voting: UgcVotingApi = {
    vote: async (params: UgcVoteParams): Promise<UgcVoteMutationResult> => {
      return this.request<UgcVoteMutationResult>(
        'POST',
        `${this.basePath}/voting/vote`,
        omitUndefined({
          entryId: params.entryId,
          count: params.count,
          clientRequestId: params.clientRequestId,
        }),
      )
    },
    unvote: async (params: UgcUnvoteParams): Promise<UgcVoteMutationResult> => {
      return this.request<UgcVoteMutationResult>(
        'POST',
        `${this.basePath}/voting/unvote`,
        omitUndefined({
          entryId: params.entryId,
          count: params.count,
          clientRequestId: params.clientRequestId,
        }),
      )
    },
    getMyVotes: async (): Promise<UgcMyVotesResponse> => {
      return this.request<UgcMyVotesResponse>('GET', `${this.basePath}/voting/me`)
    },
    getLeaderboard: async (params?: UgcVoteListParams): Promise<UgcVoteLeaderboardResponse> => {
      const qs = new URLSearchParams()
      if (params?.limit !== undefined) qs.set('limit', String(params.limit))
      if (params?.cursor) qs.set('cursor', params.cursor)
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request<UgcVoteLeaderboardResponse>(
        'GET',
        `${this.basePath}/voting/leaderboard${suffix}`,
      )
    },
    getWinners: async (params?: UgcVoteWinnersListParams): Promise<UgcVoteWinnersResponse> => {
      const qs = new URLSearchParams()
      if (params?.limit !== undefined) qs.set('limit', String(params.limit))
      if (params?.cursor) qs.set('cursor', params.cursor)
      if (params?.topN !== undefined) qs.set('topN', String(params.topN))
      const suffix = qs.toString() ? `?${qs.toString()}` : ''
      return this.request<UgcVoteWinnersResponse>(
        'GET',
        `${this.basePath}/voting/winners${suffix}`,
      )
    },
  }
}
